import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import RulesTab from './components/RulesTab';
import StoreTab from './components/StoreTab';
import HabitLevelTab from './components/HabitLevelTab';
import { useAuth } from '@/lib/api/auth';
import { api } from '@/lib/api';
import { useFamilyData } from '@/lib/context/FamilyDataContext';
import type { RewardRuleInfo, PunishRuleInfo, ExchangeItemInfo, ChildInfo, ChildLevelInfo } from '@/lib/api/types';

const tabs = [
  { id: 'rules', label: '奖惩规则', icon: 'ri-list-check-3' },
  { id: 'store', label: '积分商城', icon: 'ri-store-3-fill' },
  { id: 'levels', label: '习惯等级', icon: 'ri-vip-crown-fill' }
];

export default function RewardsPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'rules';
  const [activeTab, setActiveTab] = useState(initialTab);
  const { currentFamily } = useAuth();
  const { children, rewardRules, punishRules, ensureRulesLoaded, refreshRules } = useFamilyData(); // 从共享Context获取

  const [loading, setLoading] = useState(true);
  const [exchangeItems, setExchangeItems] = useState<ExchangeItemInfo[]>([]);
  const [levels, setLevels] = useState<ChildLevelInfo[]>([]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // 进入页面时加载数据
  useEffect(() => {
    if (!currentFamily) return;
    ensureRulesLoaded(); // 延迟加载规则
    loadData(true);
  }, [currentFamily]);

  const loadData = async (isInitial = false) => {
    if (!currentFamily) return;
    if (isInitial) setLoading(true);
    try {
      // 刷新规则数据
      await refreshRules();
      // 只获取不在共享Context中的数据
      const [itemsRes, levelsRes] = await Promise.all([
        api.exchange.getExchangeItems(currentFamily.id),
        api.family.getLevels(currentFamily.id),
      ]);
      if (itemsRes.code === 0) setExchangeItems(itemsRes.data);
      if (levelsRes.code === 0) setLevels(levelsRes.data);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      if (isInitial) {
        alert('加载数据失败: ' + (error?.response?.data?.detail || error?.message || '未知错误'));
      }
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const totalRewards = rewardRules.length;
  const totalPunishments = punishRules.length;
  const totalStore = exchangeItems.filter(i => i.status === 1).length;
  const enabledRules = [...rewardRules, ...punishRules].filter(r => r.status === 1).length;

  const stats = [
    { label: '奖励规则', value: totalRewards, icon: 'ri-trophy-fill', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    { label: '惩罚规则', value: totalPunishments, icon: 'ri-alert-fill', color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-100' },
    { label: '商城奖品', value: totalStore, icon: 'ri-gift-2-fill', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-100' },
    { label: '已启用规则', value: enabledRules, icon: 'ri-toggle-fill', color: 'text-[#1a56db]', bg: 'bg-blue-50', border: 'border-blue-100' }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="ri-loader-4-line animate-spin text-4xl text-blue-500 mb-3"></i>
              <p className="text-gray-500">加载中...</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">奖惩机制</h1>
            <p className="text-gray-400 text-sm mt-0.5">管理家庭积分奖惩规则与积分商城</p>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {stats.map((s, i) => (
            <div key={i} className={`bg-white rounded-2xl p-3 md:p-4 flex items-center gap-3 border ${s.border}`}>
              <div className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-xl ${s.bg} flex-shrink-0`}>
                <i className={`${s.icon} ${s.color} text-lg md:text-xl`}></i>
              </div>
              <div className="min-w-0">
                <div className={`text-xl md:text-2xl font-black ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-400 whitespace-nowrap">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-blue-50 p-1 rounded-xl w-fit mb-5">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'rules' && (
          <RulesTab
            rewardRules={rewardRules}
            punishRules={punishRules}
            onRefresh={loadData}
          />
        )}
        {activeTab === 'store' && (
          <StoreTab
            items={exchangeItems}
            children={children}
            onRefresh={loadData}
          />
        )}
        {activeTab === 'levels' && (
          <HabitLevelTab
            children={children}
            levels={levels}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
